import { useLocation, useNavigate } from "react-router-dom";
import RouteDetails from "./RouteDetails";
import StopsTable from "./StopsTable";
import NextStopTable from "./NextStopTable";
import NextStopTableScreen from "./NextStopVehicleScreen";
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  GoogleMap,
  useLoadScript,
  Marker,
  DirectionsRenderer,
  OverlayView,
  Polyline,
} from "@react-google-maps/api";
import config from "../../config.json";
import axios from "axios";
import DashboardHeader from "../../components/header/DashboardHeader";
import ActiveVehicleIcon from "../../images/vehicle/Map_Vehicle_active.svg";
import BusIcon from "../../images/bus-svgrepo-com.svg";
import {
  CircularProgress,
  Box,
  Grid,
  Paper,
  Container,
  Typography,
} from "@mui/material";
import NavMenu from "../../components/navbar/NavMenu";
import TitleBar from "../../components/navbar/TitleBar";
import SubTitle from "../../components/common/SubTitle";
import MonitorSubtitle from "../../components/common/MonitorSubtitle";
import VehicleDetailsOverlay from "../../components/vehicle/VehicleDetailsOverlay";
const mapContainerStyle = {
  width: "100%",
  height: "68.5vh",
  border: "1px solid #000000",
  marginTop: "20px",
  borderRadius: "20px"
};

const mapStyles = [
  {
    featureType: "all",
    elementType: "all",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ visibility: "on" }, { color: "black" }],
  },
];

const libraries = ["places", "directions"];

// Constants for path tracking
const POSITION_HISTORY_SIZE = 25;
const DIRECTION_CHECK_THRESHOLD = 4;
const MOVEMENT_THRESHOLD = 10; // 5 meters
const TERMINAL_THRESHOLD = 20; // 30 meters near terminal
const STOP_DETAILS_FETCH_INTERVAL = 50000; // 50 seconds
const RECONNECT_INTERVAL = 5000;
const MAX_RECONNECT_ATTEMPTS = 25;

const DeviceLocationMap = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [position, setPosition] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [route, setRoute] = useState(null);
  const [stops, setStops] = useState([]);
  const [userState, setUserState] = useState(null);
  const [hasMapPermission, setHasMapPermission] = useState(false);
  const [speed, setSpeed] = useState(null);
  const [directions, setDirections] = useState(null);
  const [stopDetails, setStopDetails] = useState({});
  const [previousPosition, setPreviousPosition] = useState(null);
  const [heading, setHeading] = useState(0);
  const [map, setMap] = useState(null);
  const [snappedPosition, setSnappedPosition] = useState(null);
  const [routePaths, setRoutePaths] = useState({ before: [], after: [] });
  const [isInbound, setIsInbound] = useState(false);
  const [isMoving, setIsMoving] = useState(true);
  const [lastMovedPosition, setLastMovedPosition] = useState(null);
  const [stoppedDuration, setStoppedDuration] = useState(0);
  const [positionHistory, setPositionHistory] = useState([]);
  const [fullPath, setFullPath] = useState([]);
  const [pathInitialized, setPathInitialized] = useState(false);
  const [pathDirection, setPathDirection] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isReconnecting, setIsReconnecting] = useState(false);

  const [showVehicleDetails, setShowVehicleDetails] = useState(false);
  const [vehicleSchedule, setVehicleSchedule] = useState(null);

  // Get organization ID from localStorage
  const parsedData = JSON.parse(localStorage.getItem("userState"));
  const orgId = parsedData?.user?.organizationId;

  // Refs for tracking state
  const isMountedRef = useRef(true);
  const directionCheckRef = useRef(false);
  const directionsRef = useRef(directions);
  const eventSourceRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);

  // Update directionsRef when directions change
  useEffect(() => {
    directionsRef.current = directions;
  }, [directions]);

  // Load Google Maps Script
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: config.googleMapsApiKey,
    libraries,
  });

  // Map load callback
  const onLoad = useCallback((map) => {
    setMap(map);
  }, []);
  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  // Initialize user state and permissions
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userState"));
    if (user) {
      setUserState(user);
      const mapPermission = user.features.some(
        (feature) =>
          feature.api_path === "/api/stop/organization" &&
          feature.action === "GET"
      );
      setHasMapPermission(mapPermission);
    }
  }, []);

  // Fetch route data
  const getRouteById = async (id) => {
    try {
      const resp = await axios.get(`${config.baseURL}/route/id`, {
        params: { id },
        withCredentials: true,
      });
      return resp.data;
    } catch (error) {
      console.error("Error fetching route:", error);
      return null;
    }
  };

  // Fetch stops for route
  const fetchStopsForRoute = async (route) => {
    if (!route?.routeCoordinates) return [];
    const stops = [];
    for (const coordinate of route.routeCoordinates) {
      try {
        const stopsResponse = await axios.get(
          `${config.baseURL}/stop/organization/location`,
          {
            params: {
              organizationId: orgId,
              longitude: coordinate.longitude,
              latitude: coordinate.latitude,
            },
            withCredentials: true,
          }
        );
        const stopData = stopsResponse.data;
        stops.push({
          stopId: stopData.stopId || stopData.id,
          name: stopData.name,
          latitude: stopData.latitude,
          longitude: stopData.longitude,
        });
      } catch (error) {
        console.error('Error fetching stops:', error);
      }
    }
    return stops;
  };

  const visibleStops = stops.filter(stop => {
    if (pathDirection === "source_to_dest") {
      return stop.name !== "Manipal Hospital";
    } else if (pathDirection === "dest_to_source") {
      return stop.name !== "BIAL-T1-Departure";
    }
    return true; // Show all stops if pathDirection is null or undefined
  });

  useEffect(() => {
    const fetchVehicleData = async () => {
      if (!location.state) return;
      try {
        const response = await axios.get(`${config.baseURL}/vehicle/id`, {
          params: {
            vehicleId: location.state,
            organizationId: orgId,
          },
          withCredentials: true,
        });
        const vehicleData = response.data.vehicle;
        setVehicle(vehicleData);
        setVehicleSchedule(vehicleData.vehicleSchedule); // Store schedule
        const routeData = await getRouteById(vehicleData.routeId);
        if (routeData) {
          setRoute(routeData);
          const stopsData = await fetchStopsForRoute(routeData);
          setStops(stopsData);
        }
      } catch (error) {
        console.error('Error fetching vehicle data:', error);
      }
    };

    fetchVehicleData();
    const intervalId = setInterval(fetchVehicleData, 5 * 60 * 1000); // Every 5 minutes

    return () => clearInterval(intervalId);
  }, [location.state, orgId]);

  // Calculate distance between two points (in meters)
  const calculateDistance = useCallback((p1, p2) => {
    if (!p1 || !p2) return Infinity;
    const R = 6371e3; // Earth's radius in meters
    const φ1 = (p1.lat * Math.PI) / 180;
    const φ2 = (p2.lat * Math.PI) / 180;
    const Δφ = ((p2.lat - p1.lat) * Math.PI) / 180;
    const Δλ = ((p2.lng - p1.lng) * Math.PI) / 180;
    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }, []);

  // Find nearest point on polyline
  const
    findNearestPointOnRoute = useCallback(
      (position, directions) => {
        if (!position || !directions?.routes?.[0]) return null;
        const route = directions.routes[0];
        let closestPoint = null;
        let minDistance = Infinity;
        let routeIndex = -1;
        let currentRouteIndex = 0;
        route.legs.forEach((leg) => {
          leg.steps.forEach((step) => {
            const path = step.path;
            path.forEach((point, index) => {
              if (index === 0) return;
              const start = {
                lat: path[index - 1].lat(),
                lng: path[index - 1].lng(),
              };
              const end = {
                lat: point.lat(),
                lng: point.lng()
              };
              const dx = end.lng - start.lng;
              const dy = end.lat - start.lat;
              const segmentLength = Math.sqrt(dx * dx + dy * dy);
              if (segmentLength === 0) return;
              const t = ((position.lng - start.lng) * dx + (position.lat - start.lat) * dy) /
                (segmentLength * segmentLength);
              if (t < 0) {
                const distToStart = calculateDistance(position, start);
                if (distToStart < minDistance) {
                  minDistance = distToStart;
                  closestPoint = start;
                  routeIndex = currentRouteIndex;
                }
                return;
              }
              if (t > 1) {
                const distToEnd = calculateDistance(position, end);
                if (distToEnd < minDistance) {
                  minDistance = distToEnd;
                  closestPoint = end;
                  routeIndex = currentRouteIndex + 1;
                }
                return;
              }
              const projectedPoint = {
                lat: start.lat + t * dy,
                lng: start.lng + t * dx,
              };
              const distance = calculateDistance(position, projectedPoint);
              if (distance < minDistance) {
                minDistance = distance;
                closestPoint = projectedPoint;
                routeIndex = currentRouteIndex + t;
              }
              currentRouteIndex++;
            });
          });
        });
        const MAX_SNAP_DISTANCE = 50; // meters
        if (minDistance <= MAX_SNAP_DISTANCE) {
          return {
            ...closestPoint,
            routeIndex: routeIndex
          };
        }
        return null;
      },
      [calculateDistance]
    );

  // Check if the vehicle is moving
  const detectVehicleMovement = useCallback(
    (newPosition, lastPosition) => {
      if (!lastPosition || !newPosition) return true;
      const distance = calculateDistance(newPosition, lastPosition);
      return distance > MOVEMENT_THRESHOLD;
    },
    [calculateDistance]
  );

  // Extract route path points from directions
  const extractPathPoints = useCallback((directions) => {
    if (!directions?.routes?.[0]?.legs) return [];
    let points = [];
    directions.routes[0].legs.forEach((leg) => {
      leg.steps.forEach((step) => {
        const path = step.path;
        path.forEach((point) => {
          points.push({
            lat: point.lat(),
            lng: point.lng(),
          });
        });
      });
    });
    return points;
  }, []);

  // Check if the vehicle has completed its journey
  const isJourneyComplete = useCallback(
    (currentPosition) => {
      if (!stops || stops.length === 0 || !currentPosition || !pathDirection) {
        console.log("isJourneyComplete: missing prerequisites", {
          hasStops: !!stops && stops.length > 0,
          hasPosition: !!currentPosition,
          pathDirection
        });
        return false;
      }
      const finalStop =
        pathDirection === "source_to_dest" ? stops[stops.length - 1] : stops[0];
      const finalStopPosition = {
        lat: parseFloat(finalStop.latitude),
        lng: parseFloat(finalStop.longitude),
      };
      const distanceToFinal = calculateDistance(currentPosition, finalStopPosition);
      const TERMINAL_RADIUS = 70;
      const routeCompletionRatio = routePaths.before.length / (routePaths.before.length + routePaths.after.length);
      console.log(`Route completion: ${(routeCompletionRatio * 100).toFixed(1)}%`);
      let isNearTerminalSection = false;
      if (fullPath.length > 0) {
        const terminalSectionStartIdx = Math.floor(fullPath.length * 0.9);
        const terminalSection = fullPath.slice(terminalSectionStartIdx);
        let minDistanceToTerminalSection = Infinity;
        terminalSection.forEach(point => {
          const distance = calculateDistance(currentPosition, point);
          minDistanceToTerminalSection = Math.min(minDistanceToTerminalSection, distance);
        });
        isNearTerminalSection = minDistanceToTerminalSection < TERMINAL_RADIUS;
        console.log(`Min distance to terminal section: ${minDistanceToTerminalSection}m`);
      }
      const isCloseToFinalStop = distanceToFinal < TERMINAL_RADIUS;
      const hasCompletedMostOfRoute = routeCompletionRatio > 0.9;
      const isStoppedNearEnd = !isMoving && (isCloseToFinalStop || isNearTerminalSection) && stoppedDuration > 30;
      console.log(`Journey completion check:
        - Distance to final stop (${finalStop.name}): ${distanceToFinal}m
        - Close to final stop: ${isCloseToFinalStop}
        - Near terminal section: ${isNearTerminalSection}
        - Route mostly completed: ${hasCompletedMostOfRoute}
        - Stopped near end: ${isStoppedNearEnd}
      `);
      const isComplete =
        (isCloseToFinalStop && hasCompletedMostOfRoute) ||
        (isNearTerminalSection && hasCompletedMostOfRoute) ||
        isStoppedNearEnd;
      if (isComplete) {
        console.log("JOURNEY COMPLETE DETECTED");
      }
      return isComplete;
    },
    [stops, pathDirection, calculateDistance, routePaths.before, routePaths.after,
      fullPath, isMoving, stoppedDuration]
  );

  // Determine vehicle direction based on position changes
  const checkDirectionChange = useCallback(
    (positions) => {
      if (
        positions.length < DIRECTION_CHECK_THRESHOLD ||
        !stops ||
        stops.length < 2
      ) {
        return null;
      }
      const source = {
        lat: parseFloat(stops[0].latitude),
        lng: parseFloat(stops[0].longitude),
      };
      const destination = {
        lat: parseFloat(stops[stops.length - 1].latitude),
        lng: parseFloat(stops[stops.length - 1].longitude),
      };
      const firstPos = positions[0];
      const lastPos = positions[positions.length - 1];
      const firstPosToSource = calculateDistance(firstPos, source);
      const firstPosToDest = calculateDistance(firstPos, destination);
      const lastPosToSource = calculateDistance(lastPos, source);
      const lastPosToDest = calculateDistance(lastPos, destination);
      const sourceDistanceChange = lastPosToSource - firstPosToSource;
      const destDistanceChange = lastPosToDest - firstPosToDest;
      if (
        Math.abs(sourceDistanceChange) > 10 ||
        Math.abs(destDistanceChange) > 10
      ) {
        if (sourceDistanceChange > 0 && destDistanceChange < 0) {
          return "source_to_dest";
        } else if (sourceDistanceChange < 0 && destDistanceChange > 0) {
          return "dest_to_source";
        }
      }
      return null;
    },
    [stops, calculateDistance]
  );

  // Split the route at the current position
  const splitRouteAtPosition = useCallback(
    (allPoints, position) => {
      if (!allPoints.length || !position) {
        return { before: [], after: [] };
      }
      if (!pathDirection) {
        return { before: allPoints, after: [] };
      }
      let splitIndex = 0;
      let minDistance = Infinity;
      allPoints.forEach((point, index) => {
        const distance = calculateDistance(position, point);
        if (distance < minDistance) {
          minDistance = distance;
          splitIndex = index;
        }
      });
      if (pathDirection === "source_to_dest") {
        return {
          before: allPoints.slice(0, splitIndex + 1),
          after: allPoints.slice(splitIndex)
        };
      } else {
        return {
          before: allPoints.slice(splitIndex),
          after: allPoints.slice(0, splitIndex + 1)
        };
      }
    },
    [pathDirection, calculateDistance]
  );

  // Fetch vehicle location via SSE
  const fetchLocation = useCallback(async () => {
    if (!vehicle?.device_id || eventSourceRef.current) return;

    const connectSSE = () => {
      const query = `vehicleId=${vehicle.device_id}`;
      const sseUrl = `${config.baseURL}/redis/stream?${query}`;
      const eventSource = new EventSource(sseUrl);
      eventSourceRef.current = eventSource;

      eventSource.onmessage = (event) => {
        try {
          const locationData = JSON.parse(event.data);
          console.log("SSE Data Received:", locationData);
          if (!isMountedRef.current) return;

          const newPosition = {
            lat: parseFloat(locationData?.Latitude || locationData?.latitude),
            lng: parseFloat(locationData?.Longitude || locationData?.longitude),
            timestamp: Date.now(),
          };

          if (!newPosition.lat || !newPosition.lng) {
            console.error("Invalid position data:", newPosition);
            return;
          }

          setIsOnline(true);
          setIsReconnecting(false);
          reconnectAttemptsRef.current = 0;

          setPositionHistory((prev) => {
            const updated = [...prev, newPosition].slice(-POSITION_HISTORY_SIZE);
            console.log("Position History Updated:", updated);
            return updated;
          });

          const speed = parseFloat(locationData?.Speed) || 0;
          setSpeed(speed);

          const moving = detectVehicleMovement(newPosition, lastMovedPosition);
          console.log("Movement Check:", { moving, speed, newPosition });
          if (moving) {
            setIsMoving(true);
            setLastMovedPosition(newPosition);
            setStoppedDuration(0);
          } else {
            setIsMoving(false);
            setStoppedDuration((prev) => prev + 10);
          }

          if (directionsRef.current) {
            const snapped = findNearestPointOnRoute(newPosition, directionsRef.current);
            if (snapped) {
              setPreviousPosition(position);
              setSnappedPosition(snapped);
              setPosition({
                ...newPosition,
                lat: snapped.lat,
                lng: snapped.lng,
              });
              console.log("Snapped Position:", snapped);
              if (previousPosition) {
                const dx = snapped.lng - previousPosition.lng;
                const dy = snapped.lat - previousPosition.lat;
                const angle = Math.atan2(dy, dx) * (180 / Math.PI);
                setHeading(angle);
              }
            } else if (
              position &&
              calculateDistance(position, newPosition) > MOVEMENT_THRESHOLD * 2
            ) {
              setPreviousPosition(position);
              setPosition(newPosition);
            }
          } else {
            setPosition(newPosition);
          }

          if (map) {
            map.panTo(newPosition);
          }
        } catch (error) {
          console.error("Error processing SSE data:", error);
        }
      };

      eventSource.onerror = () => {
        setIsOnline(false);
        setIsReconnecting(true);
        eventSource.close();
        eventSourceRef.current = null;
        if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          reconnectAttemptsRef.current += 1;
          console.log(`Reconnecting SSE, attempt ${reconnectAttemptsRef.current}`);
          setTimeout(connectSSE, RECONNECT_INTERVAL);
        } else {
          console.error("Max reconnect attempts reached");
          setIsReconnecting(false);
        }
      };
    };

    connectSSE();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [
    vehicle?.device_id,
    findNearestPointOnRoute,
    position,
    previousPosition,
    lastMovedPosition,
    detectVehicleMovement,
    calculateDistance,
    map,
  ]);

  // Start SSE connection
  useEffect(() => {
    if (!vehicle?.device_id) return;
    isMountedRef.current = true;
    const cleanup = fetchLocation();
    return () => {
      isMountedRef.current = false;
      if (typeof cleanup === "function") cleanup();
    };
  }, [vehicle?.device_id, fetchLocation]);

  // Initialize directions
  useEffect(() => {
    if (!isLoaded || !stops?.length || !route?.routeCoordinates) return;

    const directionsService = new window.google.maps.DirectionsService();
    // Use all stops (including non-stop points) as waypoints
    const waypoints = stops.map((stop) => ({
      location: new window.google.maps.LatLng(
        parseFloat(stop.latitude),
        parseFloat(stop.longitude)
      ),
      stopover: stop.isStop || false, // Set stopover based on isStop flag
    }));

    const request = {
      origin: waypoints[0].location,
      destination: waypoints[waypoints.length - 1].location,
      waypoints: waypoints.slice(1, -1), // Exclude origin and destination
      travelMode: window.google.maps.TravelMode.DRIVING,
      optimizeWaypoints: false, // Ensure waypoints are followed in order
    };

    directionsService.route(request, (result, status) => {
      if (status === window.google.maps.DirectionsStatus.OK) {
        setDirections(result);
        const allPoints = extractPathPoints(result);
        setFullPath(allPoints);
        if (!position && map) {
          map.setCenter(waypoints[0].location);
        }
      } else {
        console.error("Directions request failed:", status);
        // Fallback to Polyline if Directions Service fails
        const fallbackPath = route.routeCoordinates.map((coord) => ({
          lat: parseFloat(coord.latitude),
          lng: parseFloat(coord.longitude),
        }));
        setFullPath(fallbackPath);
      }
    });
  }, [stops, route, isLoaded, position, map, extractPathPoints]);

  // Clean up path direction on unmount
  useEffect(() => {
    return () => {
      if (vehicle?.device_id) {
        localStorage.removeItem(`pathDirection_${vehicle.device_id}`);
      }
      setPathDirection(null);
      setPathInitialized(false);
      setPositionHistory([]);
    };
  }, [vehicle?.device_id]);

  // Update route paths
  useEffect(() => {
    if (directions && snappedPosition) {
      const allPoints = extractPathPoints(directions);
      const split = splitRouteAtPosition(allPoints, snappedPosition);
      setRoutePaths(split);
    }
  }, [directions, snappedPosition, extractPathPoints, splitRouteAtPosition]);

  // Fetch stop details
  const fetchStopDetails = async (stop, index, totalStops) => {
    if (!vehicle?.device_id) return { distance: "N/A", eta: "N/A" };
    try {
      const response = await fetch(`${config.baseURL}/distance/time/stop`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          vehicleId: vehicle.device_id,
          longitude: stop.longitude,
          latitude: stop.latitude,
          isExtreamPoint: index === 0 || index === totalStops - 1,
        }),
      });
      if (!response.ok) {
        throw new Error(`Distance/Time API error: ${response.status}`);
      }
      const data = await response.json();
      return {
        distance: data.distance || "N/A",
        eta: data.eta || "N/A",
      };
    } catch (error) {
      console.error(`Error fetching details for stop:`, error);
      return { distance: "N/A", eta: "N/A" };
    }
  };

  // Update stop details periodically
  useEffect(() => {
    if (!stops?.length || !vehicle) return;
    const updateStopDetails = async () => {
      const details = {};
      for (let i = 0; i < stops.length; i++) {
        const stopData = await fetchStopDetails(stops[i], i, stops.length);
        details[stops[i].stopId] = stopData;
      }
      setStopDetails(details);
    };
    updateStopDetails();
    const intervalId = setInterval(
      updateStopDetails,
      STOP_DETAILS_FETCH_INTERVAL
    );
    return () => clearInterval(intervalId);
  }, [stops, vehicle]);

  // Handle direction changes and journey completion
  useEffect(() => {
    if (positionHistory.length < DIRECTION_CHECK_THRESHOLD || directionCheckRef.current) return;
    directionCheckRef.current = true;
    const currentPosition = positionHistory[positionHistory.length - 1];
    if (pathDirection && isJourneyComplete(currentPosition)) {
      console.log("Journey completed, resetting direction");
      setPathDirection(null);
      setPositionHistory([]);
      if (vehicle?.device_id) {
        localStorage.removeItem(`pathDirection_${vehicle.device_id}`);
      }
      setTimeout(() => {
        const newDirection = determineInitialDirection(currentPosition);
        if (newDirection) {
          console.log(`Starting new journey in direction: ${newDirection}`);
          setPathDirection(newDirection);
          savePathDirection(vehicle.device_id, newDirection);
        }
      }, 3000);
    } else if (!pathDirection) {
      const newDirection = checkDirectionChange(positionHistory);
      if (newDirection && vehicle?.device_id) {
        console.log(`Setting new journey direction: ${newDirection}`);
        setPathDirection(newDirection);
        savePathDirection(vehicle.device_id, newDirection);
      }
    }
    setTimeout(() => {
      directionCheckRef.current = false;
    }, 5000);
  }, [positionHistory, pathDirection, vehicle?.device_id, isJourneyComplete, checkDirectionChange]);

  // Helper to determine initial direction
  const determineInitialDirection = (currentPosition) => {
    if (!stops || stops.length < 2 || !currentPosition) return null;
    const source = {
      lat: parseFloat(stops[0].latitude),
      lng: parseFloat(stops[0].longitude),
    };
    const destination = {
      lat: parseFloat(stops[stops.length - 1].latitude),
      lng: parseFloat(stops[stops.length - 1].longitude),
    };
    const distToSource = calculateDistance(currentPosition, source);
    const distToDest = calculateDistance(currentPosition, destination);
    if (distToSource < TERMINAL_THRESHOLD && distToSource < distToDest) {
      return "source_to_dest";
    }
    if (distToDest < TERMINAL_THRESHOLD && distToDest < distToSource) {
      return "dest_to_source";
    }
    return null;
  };

  const savePathDirection = (vehicleId, direction) => {
    const key = `pathDirection_${vehicleId}`;
    const value = {
      direction: direction,
      timestamp: Date.now(),
      vehicleId: vehicleId
    };
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error("Failed to save to localStorage:", error);
    }
  };

  const getPathDirection = (vehicleId) => {
    const key = `pathDirection_${vehicleId}`;
    const saved = localStorage.getItem(key);
    if (!saved) return null;
    try {
      const value = JSON.parse(saved);
      const ageInMinutes = (Date.now() - value.timestamp) / (1000 * 60);
      if (ageInMinutes > 60) {
        localStorage.removeItem(key);
        return null;
      }
      return value.direction;
    } catch (e) {
      return null;
    }
  };

  // Restore saved direction
  useEffect(() => {
    if (vehicle?.device_id) {
      const savedDirection = getPathDirection(vehicle.device_id);
      if (savedDirection) {
        setPathDirection(savedDirection);
        console.log(`Restored direction for vehicle ${vehicle.device_id}: ${savedDirection}`);
      }
    }
  }, [vehicle?.device_id]);

  const handleVehicleView = () => {
    navigate(`/vehicle/screen/${location.state}`, {
      state: {
        vehicleId: location.state,
        organizationId: orgId,
      },
    });
  };

  if (loadError) {
    return (
      <div>
        <TitleBar />
        <NavMenu />
        <div style={{ padding: "32px" }}>
          Error loading maps. Please try again later.
        </div>
      </div>
    );
  }

  return (
    <>
      <TitleBar />
      <NavMenu />
      <MonitorSubtitle
        title="Monitor Vehicle"
        path="Vehicles > Monitor Vehicle"
        track={"Vehicle View"}
        onTrackClick={handleVehicleView}
      />
      <div style={{ padding: "0px 40px" }}>
        {hasMapPermission && (
          <>
            {!route || !stops ? (
              <div style={{ marginBottom: "20px" }}>
                <CircularProgress />
              </div>
            ) : (
              <NextStopTableScreen
                details={route}
                stops={visibleStops}
                stopDetails={stopDetails}
                isInbound={pathDirection === "dest_to_source"}
                currentPosition={snappedPosition}
                routeCoordinates={route?.routeCoordinates}
              />
            )}
            {!isLoaded ? (
              <div
                style={{
                  height: "68.5vh",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CircularProgress />
              </div>
            ) : (
              <Box sx={{
                borderRadius: "10px",
                margin: "20px 0px",
                padding: "12px 16px",
                backgroundColor: "#FFFFFF",
                border: "1px solid #A5DCF3",
                boxShadow: "-2px 1px 5px 1px #00000040"
              }}>
                <Box
                  sx={{
                    minHeight: "48px",
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: { xs: "center", sm: "space-between" },
                    alignItems: { xs: "flex-start", sm: "center" },
                    padding: { xs: "10px 16px", sm: "12px 16px" },
                    background: "#E8EAEE",
                    boxShadow: "0px 2px 5px 0px #00000040",
                    gap: { xs: 1, sm: 0 }
                  }}
                >
                  <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    mb: { xs: 1, sm: 0 }
                  }}>
                    <Typography
                      sx={{
                        color: "#414958",
                        fontFamily: "Roboto",
                        fontWeight: 400,
                        fontSize: { xs: "16px", sm: "18px", md: "20px" },
                        lineHeight: "100%",
                        letterSpacing: "0%"
                      }}
                    >
                      Vehicle Name:
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "Roboto",
                        fontWeight: 600,
                        fontSize: { xs: "16px", sm: "18px", md: "20px" },
                        lineHeight: "100%",
                        color: "#183059",
                        letterSpacing: "0%",
                        ml: 1,
                        textOverflow: "ellipsis",
                        overflow: "hidden"
                      }}
                    >
                      {vehicle?.name || "---"}
                    </Typography>
                  </Box>
                  <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap"
                  }}>
                    <Typography
                      sx={{
                        color: "#414958",
                        fontFamily: "Roboto",
                        fontWeight: 400,
                        fontSize: { xs: "16px", sm: "18px", md: "20px" },
                        lineHeight: "100%",
                        letterSpacing: "0%"
                      }}
                    >
                      Route:
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "Roboto",
                        fontWeight: 600,
                        fontSize: { xs: "16px", sm: "18px", md: "20px" },
                        lineHeight: "100%",
                        color: "#183059",
                        letterSpacing: "0%",
                        ml: 1,
                        textOverflow: "ellipsis",
                        overflow: "hidden"
                      }}
                    >
                      {route?.name || "---"}
                    </Typography>
                  </Box>
                </Box>
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={
                    position ||
                    (stops?.length
                      ? {
                        lat: parseFloat(stops[0].latitude),
                        lng: parseFloat(stops[0].longitude),
                      }
                      : null)
                  }
                  zoom={16}
                  onLoad={onLoad}
                  onUnmount={onUnmount}
                  options={{
                    styles: mapStyles,
                    disableDefaultUI: true,
                    zoomControl: true,
                    streetViewControl: true,
                    fullscreenControl: true,
                    mapTypeControl: false,
                    gestureHandling: "greedy",
                    minZoom: 15,
                    maxZoom: 40,
                    animatedZoom: true,
                    maxZoomRectangleSize: 8000,
                  }}
                >
                  {fullPath.length > 0 && !pathDirection && (
                    <Polyline
                      path={fullPath}
                      options={{
                        strokeColor: "#808080",
                        strokeOpacity: 0.7,
                        strokeWeight: 6,
                      }}
                    />
                  )}
                  {fullPath.length > 0 && pathDirection && (
                    <>
                      {routePaths.before.length > 0 && (
                        <Polyline
                          path={routePaths.before}
                          options={{
                            strokeColor: "#808080",
                            strokeOpacity: 0.7,
                            strokeWeight: 6,
                          }}
                        />
                      )}
                      {routePaths.after.length > 0 && (
                        <Polyline
                          path={routePaths.after}
                          options={{
                            strokeColor: route?.routeColor || "#2196F3",
                            strokeOpacity: 1,
                            strokeWeight: 6,
                          }}
                        />
                      )}
                    </>
                  )}
                  {!isMoving && stoppedDuration > 60 && position && (
                    <Marker
                      position={position}
                      icon={{
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: 8,
                        fillColor: "#FF0000",
                        fillOpacity: 0.7,
                        strokeWeight: 2,
                        strokeColor: "#FFFFFF",
                      }}
                      title={`Vehicle stopped for ${Math.floor(
                        stoppedDuration / 60
                      )} minutes`}
                    />
                  )}
                  {visibleStops.map((stop, index) => (
                    <React.Fragment key={`stop-${stop.id}-${index}`}>
                      <OverlayView
                        position={{
                          lat: parseFloat(stop.latitude),
                          lng: parseFloat(stop.longitude),
                        }}
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                      >
                        <div
                          style={{
                            position: "relative",
                            top: "-62px",
                            left: "0px",
                          }}
                        >
                          <div
                            style={{
                              position: "absolute",
                              left: "50%",
                              transform: "translateX(-50%)",
                              backgroundColor: "white",
                              color:
                                index === 0
                                  ? "red"
                                  : index === stops.length - 1
                                    ? "blue"
                                    : "black",
                              padding: "4px 8px",
                              borderRadius: "4px",
                              fontSize: "12px",
                              fontWeight: "500",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                              whiteSpace: "nowrap",
                              zIndex: 1000,
                            }}
                          >
                            {stop.name}
                          </div>
                        </div>
                      </OverlayView>
                      <Marker
                        key={`marker-${stop.id}-${index}`}
                        position={{
                          lat: parseFloat(stop.latitude),
                          lng: parseFloat(stop.longitude),
                        }}
                      />
                    </React.Fragment>
                  ))}
                  {position && isFinite(position.lat) && isFinite(position.lng) && (
                    <React.Fragment>
                      <OverlayView
                        position={position}
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                      >
                        <div style={{ position: "relative", top: "-60px" }}>
                          <div
                            style={{
                              position: "absolute",
                              width: "2px",
                              backgroundColor: "#20A8E0",
                              height: "75px",
                              top: "-20px",
                              left: "50%",
                              transform: "translateX(-50%)",
                            }}
                          />
                          <div
                            style={{
                              position: "absolute",
                              bottom: "20px",
                              left: "50%",
                              transform: "translateX(-50%)",
                              color: "rgb(0, 0, 0)",
                              fontSize: "10px",
                              fontWeight: 600,
                              fontFamily: "Arial, sans-serif",
                              lineHeight: "14.06px",
                              padding: "8px",
                              textAlign: "center",
                              whiteSpace: "nowrap",
                              backgroundColor: "#20A8E0",
                              borderRadius: "8px",
                              zIndex: 1000,
                              border: "1px solid black",
                            }}
                          >
                            {vehicle?.name || "Vehicle"}
                          </div>
                        </div>
                      </OverlayView>
                      <div style={{ transition: "transform 0.1s ease-in-out" }}>
                        <Marker
                          position={position}
                          icon={{
                            url: BusIcon,
                            scaledSize: new window.google.maps.Size(40, 50),
                            anchor: new window.google.maps.Point(20, 20),
                            rotation: heading - 45,
                          }}
                          onClick={() => setShowVehicleDetails(true)}
                          onMouseOver={() => setShowVehicleDetails(true)}
                          onMouseOut={() => setTimeout(() => setShowVehicleDetails(false), 500)}
                        />
                      </div>
                      {showVehicleDetails && (
                        <VehicleDetailsOverlay
                          vehicle={vehicle}
                          route={route}
                          isMoving={isMoving}
                          stoppedDuration={stoppedDuration}
                          position={position}
                        />
                      )}
                    </React.Fragment>
                  )}
                </GoogleMap>
              </Box>
            )}
            {!stops ? (
              <div style={{ marginTop: "20px", textAlign: "center" }}>
                <CircularProgress />
              </div>
            ) : (
              <StopsTable
  stops={stops}
  stopDetails={stopDetails}
  vehicleSchedule={vehicleSchedule}
  vehiclePosition={position}
  isMoving={isMoving}
  stoppedDuration={stoppedDuration}
  vehicleId={location.state} // Pass vehicleId
/>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default DeviceLocationMap;